import React, { useState, useEffect } from "react";
import { useSalaryDetail } from "../context/SalaryDetailContext";
import CurrencyInput from "components/form/currency-input";
import Swal from "sweetalert2";


const SalaryDetailManagement = ({ salaryId, categoryType, onClose, onDataChanged }) => {
  const {
    salaryDetails,
    // filterOptions,
    handleSalaryIdChange,
    handleTypeChange,
    createSalaryDetail,
    updateSalaryDetail,
    deleteSalaryDetail,
    refetch,

  } = useSalaryDetail();

  const [searchInput, setSearchInput] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    amount: "",
    quantity: 1,
    type: categoryType,
  });

  // Set salary ID and type when component mounts
  useEffect(() => {
    handleSalaryIdChange(salaryId);
    handleTypeChange(categoryType);
  }, [salaryId, categoryType]);

  const { data: detailsData, loading, error } = salaryDetails;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getCategoryTitle = (type) => {
    const titles = {
      facility: "Facilities",
      insurance: "Insurances",
      deduction: "Deductions",
      work_bonus: "Work Bonuses",
      other: "Other Bonuses",
    };
    return titles[type] || "Salary Details";
  };

  const getCategoryColor = (type) => {
    const colors = {
      facility: "info",
      insurance: "warning",
      deduction: "danger",
      work_bonus: "primary",
      other: "secondary",
    };
    return colors[type] || "secondary";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' || name === 'quantity' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        salary_id: salaryId,
        type: categoryType,
      };

      if (editingItem) {
        await updateSalaryDetail(editingItem.id, data);
      } else {
        await createSalaryDetail(data);
      }

      // Reset form and close modal
      setFormData({
        name: "",
        amount: "",
        quantity: 1,
        type: categoryType,
      });
      setIsCreateModalOpen(false);
      setEditingItem(null);
      refetch();

      // 🔥 Panggil callback untuk update tabel utama
      if (onDataChanged) {
        onDataChanged();
      }
    } catch (error) {
      console.error("Error saving salary detail:", error);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      amount: item.amount,
      quantity: item.quantity,
      type: item.type,
    });
    setIsCreateModalOpen(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteSalaryDetail(id);
        refetch();
        if (onDataChanged) {
          onDataChanged();
        }
      } catch (error) {
        console.error("Error deleting salary detail:", error);
      }
    }
  };



  const handleCancel = () => {
    setFormData({
      name: "",
      amount: "",
      quantity: 1,
      type: categoryType,
    });
    setIsCreateModalOpen(false);
    setEditingItem(null);
  };

  const filteredData = detailsData?.filter(item =>
    item.name.toLowerCase().includes(searchInput.toLowerCase())
  ) || [];

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div>
          <h3 className="card-title mb-0">
            Manage {getCategoryTitle(categoryType)}
          </h3>
        </div>

        <button
          type="button"
          className="btn-close"
          onClick={onClose}
          aria-label="Close"
        ></button>
      </div>
      <div className="card-body">
        {/* Header Actions */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="input-icon" style={{ width: '300px' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search items..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <span className="input-icon-addon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="icon icon-tabler icon-tabler-search"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <circle cx="10" cy="10" r="7" />
                <line x1="21" y1="21" x2="15" y2="15" />
              </svg>
            </span>
          </div>
          <button
            className={`btn btn-${getCategoryColor(categoryType)}`}
            onClick={() => setIsCreateModalOpen(true)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon icon-tabler icon-tabler-plus"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add {getCategoryTitle(categoryType).slice(0, -1)}
          </button>

        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-vcenter table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>Amount</th>
                <th>Quantity</th>
                <th>Total</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Loading salary details...
                  </td>
                </tr>
              ) : filteredData.length > 0 ? (
                filteredData.map((item) => (
                  <tr key={item.id}>
                    <td className="fw-bold">{item.name}</td>
                    <td>{formatCurrency(item.amount)}</td>
                    <td>{item.quantity}</td>
                    <td className={`fw-bold text-${getCategoryColor(item.type)}`}>
                      {formatCurrency(parseFloat(item.amount) * item.quantity)}
                    </td>
                    <td>
                      <div className="btn-list flex-nowrap">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(item.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No {getCategoryTitle(categoryType).toLowerCase()} found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        {filteredData.length > 0 && (
          <div className="mt-3 p-3 bg-light rounded">
            <div className="row">
              <div className="col-md-6">
                <strong>Total Items:</strong> {filteredData.length}
              </div>
              <div className="col-md-6 text-end">
                <strong>Total Amount:</strong>{" "}
                <span className={`fw-bold text-${getCategoryColor(categoryType)}`}>
                  {formatCurrency(
                    filteredData.reduce((sum, item) =>
                      sum + (parseFloat(item.amount) * item.quantity), 0
                    )
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isCreateModalOpen && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingItem ? 'Edit' : 'Add'} {getCategoryTitle(categoryType).slice(0, -1)}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancel}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Amount <span className="text-danger">*</span></label>
                    <CurrencyInput
                      name="amount"
                      value={formData.amount}
                      onChange={(e, value) => setFormData(prev => ({ ...prev, amount: value }))}
                      placeholder="Rp. 0"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Quantity <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      className="form-control"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      min="1"
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button type="submit" className={`btn btn-${getCategoryColor(categoryType)}`}>
                    {editingItem ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryDetailManagement;
